import { get as getCookie } from 'js/lib/cookie';
import { get as getUser, isAuthenticatedUser } from 'js/lib/user';

import localStorageEx from 'bundles/common/utils/localStorageEx';

export type SavedSurveyResponse = {
  // Full id the server returned, including the survey prefix (`<surveyId>~<surveyResponseId>`).
  responseId: string;
  // The section to submit next.
  sectionId: string;
  surveyVariant: string;
};

// Keeps saved answers private to one visitor on a shared computer: the user id when logged in, else the session cookie.
const getStorageScope = () => {
  if (isAuthenticatedUser()) {
    return `user.${getUser().id}`;
  }
  const sessionId = getCookie('__204u') ?? 'anonymous';
  return `session.${sessionId}`;
};

const getStorageKey = (productId: string) => `eoi.multiStepResponse.${getStorageScope()}.${productId}`;

const isSavedSurveyResponse = (value: unknown): value is SavedSurveyResponse => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const { responseId, sectionId, surveyVariant } = value as Partial<SavedSurveyResponse>;
  return typeof responseId === 'string' && typeof sectionId === 'string' && typeof surveyVariant === 'string';
};

// Returns nothing when there is no entry, or when it was saved for a different survey variant.
export const readSavedSurveyResponse = (productId: string, surveyVariant: string): SavedSurveyResponse | undefined => {
  const saved = localStorageEx.getItemValidated<SavedSurveyResponse | undefined>(
    getStorageKey(productId),
    (value): value is SavedSurveyResponse | undefined => value === undefined || isSavedSurveyResponse(value),
    undefined
  );
  return saved?.surveyVariant === surveyVariant ? saved : undefined;
};

export const writeSavedSurveyResponse = (productId: string, saved: SavedSurveyResponse) => {
  localStorageEx.setItem(getStorageKey(productId), saved, JSON.stringify);
};

export const clearSavedSurveyResponse = (productId: string) => {
  localStorageEx.removeItem(getStorageKey(productId));
};

export type CompletedSurvey = {
  surveyVariant: string;
};

const getCompletedStorageKey = (productId: string) => `eoi.multiStepCompleted.${getStorageScope()}.${productId}`;

const isCompletedSurvey = (value: unknown): value is CompletedSurvey => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const { surveyVariant } = value as Partial<CompletedSurvey>;
  return typeof surveyVariant === 'string';
};

// Returns nothing when the visitor has not finished this survey variant in this browser.
export const readCompletedSurvey = (productId: string, surveyVariant: string): CompletedSurvey | undefined => {
  const completed = localStorageEx.getItemValidated<CompletedSurvey | undefined>(
    getCompletedStorageKey(productId),
    (value): value is CompletedSurvey | undefined => value === undefined || isCompletedSurvey(value),
    undefined
  );
  return completed?.surveyVariant === surveyVariant ? completed : undefined;
};

export const writeCompletedSurvey = (productId: string, completed: CompletedSurvey) => {
  localStorageEx.setItem(getCompletedStorageKey(productId), completed, JSON.stringify);
};
